/**
 * Порядок и доли экрана «Возражения» (#846) — без React, чтобы их можно было
 * проверить отдельно от разметки.
 *
 * Рынок — о чём говорят клиенты: коды по числу разговоров, как их прислал
 * сервер. Команда — что команда роняет: тот же порядок, что у карточки
 * Quality (`compareByUnhandled`), чтобы карточка и экран называли первым один
 * и тот же код.
 */
import type {
  ObjectionStreamCode,
  ObjectionStreamResponse,
} from '@aica/api-types/schemas';
import { compareByUnhandled } from '@aica/core/voc';
import type { VoiceSection } from '../voice/voice-view-model';
import type { ObjectionsViewMode } from './objections-search';

export type { ObjectionsViewMode };

export function orderCodes(
  codes: readonly ObjectionStreamCode[],
  mode: ObjectionsViewMode
): ObjectionStreamCode[] {
  if (mode === 'market') return [...codes];
  return [...codes].sort(compareByUnhandled);
}

/** Целый процент; 0 из 0 — ноль, а не NaN. */
export function percent(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0;
}

export type ObjectionTrend =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'change'; prevShare: number; delta: number };

/**
 * Сравнение с прошлым окном — по ДОЛЕ, а не по числу: при вдвое меньшем
 * потоке звонков число упадёт у всех кодов, и «цена упала» было бы неправдой.
 * Знаменатель — разговоры с РАСПОЗНАННЫМ возражением: доля нераспознанной
 * истории в окнах разная (старые анализы без кода), и знаменатель с ней
 * выдал бы перемену в распознавании за перемену рынка.
 */
export interface TrendBase {
  recognizedCalls: number;
  prevRecognizedCalls: number | null;
  prevHasUnrecognized: boolean | null;
  prevTruncated: boolean;
}

/**
 * «Новое» — утверждение об отсутствии, и делается только тогда, когда прошлое
 * окно посчитано целиком и в нём нет нераспознанных возражений: иначе причина
 * могла быть там, просто не распознана или не попала под лимит.
 */
export function objectionTrend(
  code: Pick<ObjectionStreamCode, 'calls' | 'prevCalls'>,
  base: TrendBase
): ObjectionTrend {
  const { recognizedCalls, prevRecognizedCalls } = base;
  if (code.prevCalls === null || prevRecognizedCalls === null) return { kind: 'none' };
  if (prevRecognizedCalls === 0) return { kind: 'none' };
  if (code.prevCalls === 0) {
    const prevComplete = !base.prevTruncated && base.prevHasUnrecognized === false;
    return prevComplete ? { kind: 'new' } : { kind: 'none' };
  }
  const share = percent(code.calls, recognizedCalls);
  const prevShare = percent(code.prevCalls, prevRecognizedCalls);
  return { kind: 'change', prevShare, delta: share - prevShare };
}

/**
 * Пометка кода там, где поток спросил пометки Р3 (`marks=true`, раздел
 * «Голос клиента», #2698, решение владельца): «новое» — только по пометке
 * сервера («кода не было ни в одном покрытом прошлом периоде за год»), то же
 * утверждение, что у вида «Что изменилось». «Не было в прошлом окне» без
 * пометки — не «новое»: иначе карточка и вид называли бы новым разное.
 * Сдвиг доли против прошлого окна — как на экране «Возражения», и у кода,
 * которого в прошлом окне не было, тоже: от 0 % — это сдвиг, а не
 * утверждение об отсутствии, и сервер помечает его «выросло».
 */
export function markedObjectionTrend(
  code: Pick<ObjectionStreamCode, 'calls' | 'prevCalls' | 'mark'>,
  base: TrendBase
): ObjectionTrend {
  if (code.mark?.kind === 'new') return { kind: 'new' };
  if (code.prevCalls === 0 && base.prevRecognizedCalls) {
    const share = percent(code.calls, base.recognizedCalls);
    return { kind: 'change', prevShare: 0, delta: share };
  }
  return objectionTrend(code, base);
}

/** Последние по порядку: «стадия без фазы», затем «без этапа». */
const STAGE_TAIL = ['unmapped', 'unknown'];

/** Этапы сделки по убыванию разговоров; «без фазы» и «без этапа» — в конце. */
export function stageRows(byStage: Record<string, number>): [string, number][] {
  const tail = (key: string) => STAGE_TAIL.indexOf(key);
  return Object.entries(byStage)
    .filter(([, calls]) => calls > 0)
    .sort(([a, x], [b, y]) => tail(a) - tail(b) || y - x || a.localeCompare(b));
}

/**
 * Коды потока — блок «Возражения» вида «Что изменилось» «Голоса клиента»
 * (#2552) и выгрузки «Продаж» (#2350): строка с пометкой Р3 от сервера, N —
 * разговоры с распознанным возражением (знаменатель доли карточки),
 * разговоры строки — цитаты клиента. Порядок — взгляд «Рынок», как прислал
 * сервер; разбивки взгляда «Команда» (сотрудники, отработка) в блок не входят:
 * в них имена сотрудников, а выгрузка уходит тем, кто в Aelo не заходит.
 */
export function objectionSection(stream: ObjectionStreamResponse): VoiceSection {
  // No recognized objection: the screen's empty line, not «not collected».
  const emptyKey =
    stream.unmappedShare > 0
      ? 'objectionsPage.emptyUnrecognized'
      : 'objectionsPage.empty';
  return {
    id: 'objections',
    callsInWindow: stream.recognizedCalls,
    baseKey: 'voicePage.export.base.objections',
    zeroKey: emptyKey,
    emptyKey,
    // The stream is read by its own request: its cap is not the questions',
    // and the screen words it its own way (`ObjectionsList`).
    cap: {
      truncated: stream.truncated,
      prevTruncated: stream.prevTruncated,
      truncatedKey: 'supervisorDashboard.quality.objections.truncated',
      prevTruncatedKey: 'objectionsPage.prevTruncated',
    },
    rows: stream.codes.map((code) => ({
      id: `objection:${code.code}`,
      kind: 'objection',
      key: code.code,
      calls: code.calls,
      prevCalls: code.prevCalls ?? 0,
      callsInWindow: stream.recognizedCalls,
      prevCallsInWindow: stream.prevRecognizedCalls ?? 0,
      // A code the stream could not compare has no previous numbers.
      ...(code.prevCalls === null ? { noPrevious: true as const } : {}),
      ...(code.mark ? { mark: code.mark } : {}),
      objection: code,
      items: code.quotes.map((quote) => ({
        recordId: quote.recordId,
        createdAt: quote.createdAt,
        text: quote.quote,
        detail: null,
      })),
    })),
  };
}

/**
 * Строки печатной формы о блоке возражений — те, что экран пишет под списком
 * (#2350): сколько разговоров с возражением, от чего доля, какая часть
 * возражений не распознана или не подошла ни одной причине, сколько разговоров
 * с возражениями было в прошлом периоде. Предел выборки — в карточке блока
 * (`objectionSection`, `cap`).
 */
export function objectionPrintNotes(
  stream: ObjectionStreamResponse,
  t: (key: string, options?: Record<string, unknown>) => string,
  /** A theme filter without a comparable previous window (#2229): no previous line. */
  comparable: boolean
): string[] {
  return [
    t('objectionsPage.summary', {
      calls: stream.callsWithObjections,
      count: stream.callsInWindow,
      share: percent(stream.callsWithObjections, stream.callsInWindow),
    }),
    t('voicePage.objectionsDenominatorNote'),
    stream.unmappedShare > 0
      ? t('supervisorDashboard.quality.objections.unmapped', {
          share: Math.round(stream.unmappedShare * 100),
        })
      : '',
    stream.otherShare > 0
      ? t('supervisorDashboard.quality.objections.other', {
          share: Math.round(stream.otherShare * 100),
        })
      : '',
    comparable && stream.prevCallsWithObjections !== null
      ? t('objectionsPage.prevWindow', { count: stream.prevCallsWithObjections })
      : '',
  ].filter(Boolean);
}
